<template>
  <div class="skill-card" @click="$emit('click')">
    <div :class="['skill-icon', iconClass]">
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <component :is="iconComponent" />
      </svg>
    </div>
    <h3>
      {{ title }} <span v-if="version" class="skill-version">{{ version }}</span>
    </h3>
    <p>{{ description }}</p>
    <div class="skill-features">
      <span v-for="feature in features" :key="feature" class="skill-feature">
        {{ feature }}
      </span>
    </div>
    <div class="skill-footer">
      <span :class="['skill-status', { coming: !active }]">
        {{ active ? 'Active' : 'Coming Soon' }}
      </span>
      <span v-if="active" class="view-details">
        查看详情
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17 8l4 4m0 0l-4 4m4-4H3"
          />
        </svg>
      </span>
      <span v-else class="view-details" style="color: #64748b">
        关注动态
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17 8l4 4m0 0l-4 4m4-4H3"
          />
        </svg>
      </span>
    </div>
  </div>
</template>

<script setup>
  import { computed, h } from 'vue'

  const props = defineProps({
    title: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    features: {
      type: Array,
      default: () => []
    },
    iconType: {
      type: String,
      default: 'coming'
    },
    active: {
      type: Boolean,
      default: true
    },
    version: {
      type: String,
      default: ''
    }
  })

  defineEmits(['click'])

  const iconClass = computed(() => props.iconType)

  const iconComponent = computed(() => {
    const icons = {
      hot: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.5 17.656 5.5a8 8 0 002.686-6.67'
      }),
      social: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
      }),
      report: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
      }),
      industry: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M3 3v18h18M7 14l4-4 4 4 5-6'
      }),
      macro: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-9a13 13 0 010 18m0-18a13 13 0 000 18m-9-9h18'
      }),
      enterprise: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
      }),
      bond: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
      }),
      coming: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M12 6v6m0 0v6m0-6h6m-6 0H6'
      })
    }
    return icons[props.iconType] || icons.coming
  })
</script>

<style scoped>
  .skill-card {
    background: var(--card-bg);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid var(--card-border);
    border-radius: 24px;
    padding: 32px;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
    display: flex;
    flex-direction: column;
    min-height: 380px;
  }

  .skill-card:hover {
    transform: translateY(-8px) scale(1.02);
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(99, 102, 241, 0.3);
    box-shadow: 0 12px 40px rgba(79, 70, 229, 0.1);
  }

  [data-theme='dark'] .skill-card {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  }

  [data-theme='dark'] .skill-card:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(99, 102, 241, 0.4);
    box-shadow: 0 12px 40px rgba(79, 70, 229, 0.2);
  }

  .skill-icon {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
  }

  .skill-icon svg {
    width: 32px;
    height: 32px;
    color: white;
  }

  .skill-icon.hot {
    background: linear-gradient(135deg, #7f1d1d, #991b1b);
  }

  .skill-icon.social {
    background: linear-gradient(135deg, #1e40af, #3b82f6);
  }

  .skill-icon.report {
    background: linear-gradient(135deg, #065f46, #10b981);
  }

  .skill-icon.coming {
    background: linear-gradient(135deg, #374151, #4b5563);
  }

  .skill-icon.industry {
    background: linear-gradient(135deg, #7c2d12, #ea580c);
  }

  .skill-icon.macro {
    background: linear-gradient(135deg, #1e3a5f, #0ea5e9);
  }

  .skill-icon.enterprise {
    background: linear-gradient(135deg, #4c1d95, #8b5cf6);
  }

  .skill-icon.bond {
    background: linear-gradient(135deg, #854d0e, #eab308);
  }

  .skill-card h3 {
    font-size: 20px;
    font-weight: 800;
    color: var(--text-primary);
    margin-bottom: 12px;
  }

  .skill-version {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    margin-left: 8px;
  }

  .skill-card p {
    color: var(--text-secondary);
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 20px;
    display: -webkit-box;
    -webkit-line-clamp: 8;
    line-clamp: 8;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .skill-features {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 24px;
  }

  .skill-feature {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: rgba(241, 245, 249, 0.8);
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    border: 1px solid rgba(226, 232, 240, 0.5);
  }

  [data-theme='dark'] .skill-feature {
    background: rgba(255, 255, 255, 0.08);
    color: var(--text-secondary);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .skill-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 20px;
    border-top: 1px solid var(--border-color);
    margin-top: auto;
  }

  .skill-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 700;
    color: #10b981;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .skill-status::before {
    content: '';
    width: 8px;
    height: 8px;
    background: #10b981;
    border-radius: 50%;
    box-shadow: 0 0 10px #10b981;
  }

  .skill-status.coming {
    color: var(--text-secondary);
  }

  .skill-status.coming::before {
    background: var(--text-secondary);
    box-shadow: none;
  }

  .view-details {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 700;
    color: var(--primary);
    transition: gap 0.3s ease;
  }

  .view-details:hover {
    gap: 10px;
  }

  .view-details svg {
    width: 16px;
    height: 16px;
  }
</style>
